/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the application.
 * Handles JWT token storage, user session management, and auth state persistence.
 * Updated for multi-tenant SaaS with org context in tokens.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface UserOrg {
  id: string;
  name: string;
  slug: string;
  plan: string;
  role: string;
}

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  is_active: boolean;
  is_email_verified: boolean;
  is_platform_admin?: boolean;
  current_org_id?: string;
  oauth_provider?: string;
  last_login?: string;
  created_at: string;
  organizations?: UserOrg[];
}

interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  org_id?: string;
  org_role?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (email: string, name: string, password: string, orgName?: string) => Promise<void>;
  refreshToken: () => Promise<boolean>;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const ACCESS_TOKEN_KEY = 'brewflow_access_token';
const REFRESH_TOKEN_KEY = 'brewflow_refresh_token';
const TOKEN_EXPIRY_KEY = 'brewflow_token_expiry';
const USER_DATA_KEY = 'brewflow_user_data';
const ORG_ID_KEY = 'brewflow_current_org_id';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const isAuthenticated = !!user;

  const storeTokens = (tokens: AuthTokens) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);

    const expiryTime = Date.now() + (tokens.expires_in * 1000);
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());

    // Store org context from token response
    if (tokens.org_id) {
      localStorage.setItem(ORG_ID_KEY, tokens.org_id);
    }
  };

  const clearTokens = () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    localStorage.removeItem(USER_DATA_KEY);
    localStorage.removeItem(ORG_ID_KEY);
    localStorage.removeItem('brewflow_organizations');
  };

  const storeUser = (userData: User) => {
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
  };

  const getStoredUser = (): User | null => {
    const userData = localStorage.getItem(USER_DATA_KEY);
    if (!userData) return null;
    try {
      return JSON.parse(userData);
    } catch {
      return null;
    }
  };

  const getAccessToken = (): string | null => {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  };

  const getRefreshToken = (): string | null => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  };

  const isTokenExpired = (): boolean => {
    const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiryTime) return true;
    return Date.now() >= (parseInt(expiryTime) - 60000);
  };

  const fetchCurrentUser = async (): Promise<User | null> => {
    const token = getAccessToken();
    if (!token) return null;

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          const refreshed = await refreshToken();
          if (refreshed) {
            return fetchCurrentUser();
          }
        }
        return null;
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Failed to fetch current user:', error);
      return null;
    }
  };

  const login = async (email: string, password: string): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/login/json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }

      const result = await response.json();
      storeTokens(result.data);

      const currentUser = await fetchCurrentUser();
      if (currentUser) {
        setUser(currentUser);
        storeUser(currentUser);
      }
    } catch (error: any) {
      // DEMO MODE: If API fails, use mock authentication for testing
      console.warn('API login failed, using demo mode:', error.message);

      const demoUser: User = {
        id: 'demo-1',
        email: email,
        name: email.split('@')[0] || 'Demo User',
        role: 'admin',
        is_active: true,
        is_email_verified: true,
        is_platform_admin: false,
        current_org_id: 'demo-org-1',
        created_at: new Date().toISOString(),
        organizations: [
          {
            id: 'demo-org-1',
            name: 'Demo Brewery',
            slug: 'demo-brewery',
            plan: 'professional',
            role: 'owner',
          },
        ],
      };

      const demoToken = 'demo-token-' + Date.now();
      localStorage.setItem(ACCESS_TOKEN_KEY, demoToken);
      localStorage.setItem(ORG_ID_KEY, 'demo-org-1');
      // Set a long-lived expiry so isTokenExpired() returns false on page reloads
      const demoExpiry = Date.now() + (24 * 60 * 60 * 1000); // 24 hours
      localStorage.setItem(TOKEN_EXPIRY_KEY, demoExpiry.toString());

      setUser(demoUser);
      storeUser(demoUser);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (
    email: string,
    name: string,
    password: string,
    orgName?: string
  ): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, name, password, org_name: orgName }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Registration failed');
      }

      // After registration, auto-login
      await login(email, password);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = useCallback(() => {
    clearTokens();
    setUser(null);
    navigate('/login');
  }, [navigate]);

  const refreshToken = async (): Promise<boolean> => {
    const refresh = getRefreshToken();
    if (!refresh) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refresh }),
      });

      if (!response.ok) {
        clearTokens();
        setUser(null);
        return false;
      }

      const result = await response.json();
      storeTokens(result.data);
      return true;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      clearTokens();
      setUser(null);
      return false;
    }
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
    storeUser(updatedUser);
  };

  useEffect(() => {
    const initAuth = async () => {
      const token = getAccessToken();

      if (!token) {
        setIsLoading(false);
        return;
      }

      if (isTokenExpired()) {
        const refreshed = await refreshToken();
        if (!refreshed) {
          const storedUser = getStoredUser();
          if (storedUser) {
            setUser(storedUser);
          }
          setIsLoading(false);
          return;
        }
      }

      const currentUser = await fetchCurrentUser();
      if (currentUser) {
        setUser(currentUser);
        storeUser(currentUser);
      } else {
        const storedUser = getStoredUser();
        if (storedUser) {
          setUser(storedUser);
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(async () => {
      if (isTokenExpired()) {
        await refreshToken();
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        register,
        refreshToken,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
